/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from "react";
import pages from "../index";
import {
  Avatar,
  AvatarImage,
  useToast,
} from "../../components/ui";
import { Header } from "../../components/modules";
import Context from "../../Context";
import {
  Users,
  Shield,
  ShieldCheck,
  UserPlus,
  Trash2,
  Search,
  RefreshCw,
  Phone,
  Mail,
  Layers,
  CheckCircle2,
  XCircle,
  ChevronDown,
  AlertTriangle,
  Lock,
} from "lucide-react";

import {
  DEFAULT_MASTER_ROLES,
  getEffectiveRoles,
  getRoleInfo,
  getRolePriority,
  hasPagePermission,
} from "../../util/permissionUtils";
import { RolePermissionsManager } from "./RolePermissionsManager";

export const MASTER_ROLES = DEFAULT_MASTER_ROLES;
export { getRoleInfo };

export const Admins = () => {
  const { toast } = useToast();
  const { callApi, profileState, configs } = Context();
  const isDev = profileState?.role === "dev";
  const allRoles = useMemo(() => getEffectiveRoles(configs), [configs]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'teams' | 'add'
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoleFilter, setSelectedRoleFilter] = useState("all");
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // Search User for Adding Admin
  const [searchPhone, setSearchPhone] = useState("");
  const [searchingUser, setSearchingUser] = useState(false);
  const [candidateUsers, setCandidateUsers] = useState(null);

  useEffect(() => {
    getAdmins();
  }, []);

  const getAdmins = async () => {
    setLoading(true);
    try {
      const result = await callApi("users-getAll", {
        queries: [["admin", true]],
        limit: 500,
        orderBy: ["createdAt", "desc"],
      });
      if (Array.isArray(result)) {
        setAdmins(result);
      }
    } catch (err) {
      console.warn("Failed to get admins:", err);
    } finally {
      setLoading(false);
    }
  };

  // Edit Admin Role
  const handleEditRole = async (userId, newRole) => {
    if (!userId || !newRole) return;
    const targetUser = admins.find((a) => a.id === userId);
    if (targetUser && (targetUser.role === "dev" || targetUser.role?.toLowerCase() === "ceo")) {
      alert("ไม่สามารถแก้ไขตำแหน่งของ Developer หรือ CEO ได้ (สิทธิ์ถูกล็อก)");
      return;
    }

    if (!window.confirm(`ต้องการเปลี่ยนตำแหน่งเป็น "${getRoleInfo(newRole, configs).name}" ใช่หรือไม่?`)) {
      return;
    }

    setActionLoadingId(userId);
    try {
      const res = await callApi("users-edit", {
        id: userId,
        key: "role",
        value: newRole,
      });

      if (res?.status || res?.success) {
        toast({
          className: "bg-emerald-600 text-white rounded-md",
          title: (
            <div className="flex items-center gap-2 font-semibold text-sm">
              <CheckCircle2 className="w-4 h-4" /> อัปเดตตำแหน่งสำเร็จ
            </div>
          ),
        });
        getAdmins();
      } else {
        toast({
          className: "bg-rose-600 text-white rounded-md",
          title: (
            <div className="flex items-center gap-2 font-semibold text-sm">
              <XCircle className="w-4 h-4" /> อัปเดตตำแหน่งไม่สำเร็จ: {res?.message || "เกิดข้อผิดพลาด"}
            </div>
          ),
        });
      }
    } catch (err) {
      console.warn("Edit role error:", err);
    } finally {
      setActionLoadingId(null);
    }
  };

  // Toggle Admin Status (Grant / Revoke)
  const handleToggleAdminStatus = async (user, newStatus) => {
    if (!user?.id) return;
    if (user.role === "dev" || user.role?.toLowerCase() === "ceo") {
      alert("ไม่สามารถแก้ไขหรือลบสิทธิ์แอดมินของ Developer หรือ CEO ได้ (สิทธิ์ถูกล็อก)");
      return;
    }

    const actionText = newStatus ? "แต่งตั้งเป็นแอดมิน" : "ลบสิทธิ์แอดมิน";
    const userName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.username || user.phone;

    if (!window.confirm(`ต้องการ "${actionText}" ของคุณ ${userName} ใช่หรือไม่?`)) {
      return;
    }

    setActionLoadingId(user.id);
    try {
      const res = await callApi("users-edit", {
        id: user.id,
        key: "admin",
        value: newStatus,
      });

      if (res?.status || res?.success) {
        toast({
          className: "bg-emerald-600 text-white rounded-md",
          title: (
            <div className="flex items-center gap-2 font-semibold text-sm">
              <CheckCircle2 className="w-4 h-4" /> {actionText} สำเร็จ
            </div>
          ),
        });
        await getAdmins();
        if (candidateUsers) {
          handleSearchUser();
        }
      } else {
        toast({
          className: "bg-rose-600 text-white rounded-md",
          title: (
            <div className="flex items-center gap-2 font-semibold text-sm">
              <XCircle className="w-4 h-4" /> ทำรายการไม่สำเร็จ
            </div>
          ),
        });
      }
    } catch (err) {
      console.warn("Toggle admin status error:", err);
    } finally {
      setActionLoadingId(null);
    }
  };

  // Search User by Phone / User ID
  const handleSearchUser = async (e) => {
    if (e) e.preventDefault();
    const queryTerm = searchPhone.trim();
    if (!queryTerm) return;

    setSearchingUser(true);
    try {
      const isNumeric = /^\d+$/.test(queryTerm);
      let queryKey = "phone";
      if (isNumeric && queryTerm.length <= 8) {
        queryKey = "userId";
      }

      const result = await callApi("users-getAll", {
        queries: [[queryKey, queryTerm]],
        limit: 50,
        orderBy: ["createdAt", "desc"],
      });

      setCandidateUsers(Array.isArray(result) ? result : []);
    } catch (err) {
      console.warn("Search candidate error:", err);
      setCandidateUsers([]);
    } finally {
      setSearchingUser(false);
    }
  };

  // Filtered & Sorted Admins for Main Table
  const filteredAdmins = useMemo(() => {
    const list = admins.filter((admin) => {
      const queryStr = searchQuery.trim().toLowerCase();
      const matchQuery =
        !queryStr ||
        (admin.firstName || "").toLowerCase().includes(queryStr) ||
        (admin.lastName || "").toLowerCase().includes(queryStr) ||
        (admin.userId || "").toLowerCase().includes(queryStr) ||
        (admin.phone || "").toLowerCase().includes(queryStr) ||
        (admin.email || "").toLowerCase().includes(queryStr) ||
        (admin.lineTitle || "").toLowerCase().includes(queryStr) ||
        (admin.role || "").toLowerCase().includes(queryStr);

      const matchRole = selectedRoleFilter === "all" || admin.role === selectedRoleFilter;
      return matchQuery && matchRole;
    });

    // เรียงตามความสำคัญของตำแหน่ง (Role Hierarchy: Dev -> CEO -> superAdmin -> ...)
    return list.sort((a, b) => {
      const pA = getRolePriority(a.role);
      const pB = getRolePriority(b.role);
      if (pA !== pB) return pA - pB;
      const nameA = `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.name || "";
      const nameB = `${b.firstName || ""} ${b.lastName || ""}`.trim() || b.name || "";
      return nameA.localeCompare(nameB, "th");
    });
  }, [admins, searchQuery, selectedRoleFilter]);

  // Statistics Summary
  const stats = useMemo(() => {
    const roleCounts = {};
    admins.forEach((a) => {
      const r = a.role || "staff";
      roleCounts[r] = (roleCounts[r] || 0) + 1;
    });
    return {
      total: admins.length,
      roles: roleCounts,
      uniqueRolesCount: Object.keys(roleCounts).length,
    };
  }, [admins]);

  return (
    <div className={`w-full max-w-7xl mx-auto ${isDev ? "p-2 sm:p-6 space-y-4 sm:space-y-6" : "p-4 sm:p-6 space-y-6"} font-sans text-slate-800`}>
      {/* Header & Page Title */}
      <Header
        header="Admin & Permissions"
        subHeader="จัดการสิทธิ์พนักงาน ทีมงาน และการเข้าถึงระบบ"
        icon={<ShieldCheck className="w-6 h-6 text-blue-600" />}
      />

      {/* KPI Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        {/* Card 1: Total Admins */}
        <div className="bg-white border border-slate-200 rounded-md p-3 sm:p-4 flex items-center justify-between shadow-xs min-w-0">
          <div className="space-y-0.5 sm:space-y-1 min-w-0">
            <p className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">
              แอดมินทั้งหมด
            </p>
            <div className="flex items-baseline gap-1">
              <span className="text-xl sm:text-2xl font-bold text-slate-900">{stats.total}</span>
              <span className="text-xs text-slate-500">คน</span>
            </div>
            <p className="text-[10px] text-slate-400 truncate hidden xs:block">เข้าใช้งานระบบ</p>
          </div>
          <div className="p-2 sm:p-2.5 bg-blue-50 border border-blue-100 rounded-md text-blue-600 shrink-0">
            <Users className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
        </div>

        {/* Card 2: Active Teams */}
        <div className="bg-white border border-slate-200 rounded-md p-3 sm:p-4 flex items-center justify-between shadow-xs min-w-0">
          <div className="space-y-0.5 sm:space-y-1 min-w-0">
            <p className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">
              ตำแหน่ง / แผนก
            </p>
            <div className="flex items-baseline gap-1">
              <span className="text-xl sm:text-2xl font-bold text-indigo-600">{stats.uniqueRolesCount}</span>
              <span className="text-xs text-slate-500">กลุ่ม</span>
            </div>
            <p className="text-[10px] text-slate-400 truncate hidden xs:block">แบ่งตามบทบาท</p>
          </div>
          <div className="p-2 sm:p-2.5 bg-indigo-50 border border-indigo-100 rounded-md text-indigo-600 shrink-0">
            <Layers className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
        </div>

        {/* Card 3: Team Lead / Admins */}
        <div className="bg-white border border-slate-200 rounded-md p-3 sm:p-4 flex items-center justify-between shadow-xs min-w-0">
          <div className="space-y-0.5 sm:space-y-1 min-w-0">
            <p className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">
              Admin & Lead
            </p>
            <div className="flex items-baseline gap-1">
              <span className="text-xl sm:text-2xl font-bold text-amber-600">
                {(stats.roles.admin || 0) + (stats.roles.superAdmin || 0)}
              </span>
              <span className="text-xs text-slate-500">คน</span>
            </div>
            <p className="text-[10px] text-slate-400 truncate hidden xs:block">ผู้ดูแลออเดอร์/ลูกค้า</p>
          </div>
          <div className="p-2 sm:p-2.5 bg-amber-50 border border-amber-100 rounded-md text-amber-600 shrink-0">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
        </div>

        {/* Card 4: Quick Add Action */}
        <div
          onClick={() => setActiveTab("add")}
          className="bg-gradient-to-br from-blue-600 to-blue-700 active:from-blue-700 active:to-blue-800 text-white rounded-md p-3 sm:p-4 flex items-center justify-between shadow-xs cursor-pointer hover:shadow-md transition-all min-w-0"
        >
          <div className="space-y-0.5 sm:space-y-1 min-w-0">
            <p className="text-[10px] sm:text-xs font-bold text-blue-100 uppercase tracking-wider truncate">
              เพิ่มแอดมินใหม่
            </p>
            <h3 className="text-base sm:text-lg font-bold text-white truncate">แต่งตั้งแอดมิน</h3>
            <p className="text-[10px] text-blue-100 truncate hidden xs:block">ค้นหาเพื่อมอบสิทธิ์</p>
          </div>
          <div className="p-2 sm:p-2.5 bg-white/15 rounded-md text-white border border-white/25 shrink-0">
            <UserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
        </div>
      </div>

      {/* Main Container: Tabs & Data Sections */}
      <div className="space-y-4">
        {/* Navigation Tabs Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-200 pb-2.5 gap-2">
          <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-1 sm:pb-0 scrollbar-none max-w-full">
            <button
              onClick={() => setActiveTab("all")}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-md text-xs font-bold transition-colors whitespace-nowrap shrink-0 min-h-[38px] ${
                activeTab === "all"
                  ? "bg-blue-50 text-blue-600 border border-blue-200 shadow-xs"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              <Users className="w-4 h-4" />
              <span>พนักงานทั้งหมด</span>
              <span className="px-1.5 py-0.2 rounded-md bg-white border border-slate-200 text-[10px] text-slate-600 font-mono">
                {admins.length}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("teams")}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-md text-xs font-bold transition-colors whitespace-nowrap shrink-0 min-h-[38px] ${
                activeTab === "teams"
                  ? "bg-blue-50 text-blue-600 border border-blue-200 shadow-xs"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              <Layers className="w-4 h-4" />
              <span>แยกตามทีม</span>
            </button>

            <button
              onClick={() => setActiveTab("permissions")}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-md text-xs font-bold transition-colors whitespace-nowrap shrink-0 min-h-[38px] ${
                activeTab === "permissions"
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-300 shadow-xs font-bold"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span>สิทธิ์การเข้าถึง (Permissions)</span>
            </button>

            <button
              onClick={() => setActiveTab("add")}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-md text-xs font-bold transition-colors whitespace-nowrap shrink-0 min-h-[38px] ${
                activeTab === "add"
                  ? "bg-blue-50 text-blue-600 border border-blue-200 shadow-xs"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              <UserPlus className="w-4 h-4" />
              <span>เพิ่มแอดมิน</span>
            </button>
          </div>

          <button
            onClick={getAdmins}
            className="self-end sm:self-auto flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-md text-xs font-semibold transition-colors shadow-xs min-h-[36px]"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-navy" : ""}`} />
            <span>รีเฟรชข้อมูล</span>
          </button>
        </div>

        {/* TAB 1: All Admins Table & Cards */}
        {activeTab === "all" && (
          <div className="space-y-4">
            {/* Filters Bar */}
            <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 sm:gap-3 shadow-xs">
              {/* Search Bar */}
              <div className="relative w-full sm:w-80">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="ค้นหาชื่อ, เบอร์โทร, User ID, อีเมล..."
                  className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-400 transition-colors min-h-[38px]"
                />
              </div>

              {/* Role Filter Dropdown */}
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <span className="text-xs text-slate-500 font-semibold whitespace-nowrap">ตำแหน่ง:</span>
                <select
                  value={selectedRoleFilter}
                  onChange={(e) => setSelectedRoleFilter(e.target.value)}
                  className="w-full sm:w-auto bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-md px-2.5 py-2 focus:outline-none focus:border-blue-400 min-h-[38px]"
                >
                  <option value="all">ทุกตำแหน่ง ({admins.length})</option>
                  {allRoles.map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.name} ({stats.roles[r.key] || 0})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Admins Table & Mobile Cards */}
            {loading ? (
              <div className="py-12 text-center text-slate-400 bg-white border border-slate-200 rounded-md">
                <div className="flex flex-col items-center justify-center gap-2">
                  <RefreshCw className="w-6 h-6 animate-spin text-navy" />
                  <span className="text-xs">กำลังโหลดข้อมูลแอดมิน...</span>
                </div>
              </div>
            ) : filteredAdmins.length === 0 ? (
              <div className="py-12 text-center text-slate-400 bg-white border border-slate-200 rounded-md">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Users className="w-8 h-8 text-slate-300" />
                  <span className="text-xs">ไม่พบข้อมูลแอดมินตามเงื่อนไขที่ค้นหา</span>
                </div>
              </div>
            ) : (
              <>
                {/* Mobile Card List (< md) (Dev only) */}
                {isDev && (
                  <div className="space-y-3 block md:hidden">
                    {filteredAdmins.map((user) => (
                      <AdminMobileCard
                        key={user.id || user.userId || user.phone}
                        user={user}
                        actionLoading={actionLoadingId === user.id}
                        onEditRole={handleEditRole}
                        onRevokeAdmin={(u) => handleToggleAdminStatus(u, false)}
                        allRoles={allRoles}
                        configs={configs}
                      />
                    ))}
                  </div>
                )}

                {/* Desktop Table View (>= md, or always for non-dev) */}
                <div className={`${isDev ? "hidden md:block" : "block"} bg-white border border-slate-200 rounded-md overflow-hidden shadow-xs`}>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[650px] text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase tracking-wider font-semibold">
                          <th className="py-3 px-4">ชื่อ - นามสกุล</th>
                          <th className="py-3 px-4">เบอร์โทรศัพท์</th>
                          <th className="py-3 px-4">อีเมล / LINE</th>
                          <th className="py-3 px-4">ตำแหน่ง (Role)</th>
                          <th className="py-3 px-4 text-right">จัดการสิทธิ์</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredAdmins.map((user) => (
                          <AdminTableRow
                            key={user.id || user.userId || user.phone}
                            user={user}
                            actionLoading={actionLoadingId === user.id}
                            onEditRole={handleEditRole}
                            onRevokeAdmin={(u) => handleToggleAdminStatus(u, false)}
                            currentProfile={profileState}
                            allRoles={allRoles}
                            configs={configs}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* TAB 2: Teams & Permission Matrix */}
        {activeTab === "teams" && (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50/50 border border-blue-200 rounded-md text-xs text-blue-800 flex items-start gap-2.5">
              <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-blue-900">สรุปสิทธิ์การเข้าถึงระบบตามตำแหน่ง (Role-Based Access Control)</p>
                <p className="text-blue-700 mt-0.5">
                  ท่านสามารถคลิกดูรายชื่อสมาชิกในแต่ละทีม และตรวจสอบสิทธิ์การเข้าถึงหน้าต่างต่าง ๆ ของแต่ละบทบาทได้ด้านล่าง
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {allRoles.map((roleItem) => {
                const teamUsers = admins.filter((u) => u.role === roleItem.key);
                return (
                  <TeamAccordionItem
                    key={roleItem.key}
                    roleItem={roleItem}
                    teamUsers={teamUsers}
                    actionLoadingId={actionLoadingId}
                    onEditRole={handleEditRole}
                    onRevokeAdmin={(u) => handleToggleAdminStatus(u, false)}
                    currentProfile={profileState}
                    allRoles={allRoles}
                    configs={configs}
                    isDev={isDev}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* TAB 3: Permissions Matrix Manager */}
        {activeTab === "permissions" && <RolePermissionsManager />}

        {/* TAB 4: Add New Admin */}
        {activeTab === "add" && (
          <div className="space-y-4">
            {/* Search Box Card */}
            <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm space-y-4">
              <div className="space-y-1">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                  ค้นหาผู้ใช้งานเพื่อแต่งตั้งเป็นแอดมิน
                </h3>
                <p className="text-xs text-slate-500 font-medium">
                  ระบุเบอร์โทรศัพท์ (เช่น 0812345678) หรือ User ID เพื่อค้นหาข้อมูลผู้ใช้งานในระบบ
                </p>
              </div>

              <form onSubmit={handleSearchUser} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-md">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                    placeholder="พิมพ์เบอร์โทรศัพท์ หรือ User ID..."
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-500 transition-colors min-h-[38px]"
                  />
                </div>
                <button
                  type="submit"
                  disabled={searchingUser || !searchPhone.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-bold transition-colors shadow-sm disabled:opacity-60 flex items-center justify-center gap-1.5 min-h-[38px]"
                >
                  {searchingUser ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                  <span>ค้นหา</span>
                </button>
              </form>
            </div>

            {/* Candidate Search Results */}
            {candidateUsers !== null && (
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
                <div className="p-3.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800">
                    ผลการค้นหา ({candidateUsers.length} รายการ)
                  </span>
                </div>

                {candidateUsers.length === 0 ? (
                  <div className="p-10 text-center text-slate-400">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                    <p className="text-xs font-semibold text-slate-600">ไม่พบผู้ใช้งานตามข้อมูลที่ระบุ</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">กรุณาตรวจสอบเบอร์โทรศัพท์หรือ User ID ให้ถูกต้อง</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile View (< md) (Dev only) */}
                    {isDev && (
                      <div className="space-y-3 block md:hidden p-3 bg-slate-50/50">
                        {candidateUsers.map((user) => (
                          <CandidateMobileCard
                            key={user.id || user.userId || user.phone}
                            user={user}
                            actionLoadingId={actionLoadingId}
                            handleEditRole={handleEditRole}
                            handleToggleAdminStatus={handleToggleAdminStatus}
                            allRoles={allRoles}
                            configs={configs}
                          />
                        ))}
                      </div>
                    )}

                    {/* Desktop View (>= md, or always for non-dev) */}
                    <div className={`${isDev ? "hidden md:block" : "block"} overflow-x-auto`}>
                      <table className="w-full min-w-[650px] text-left text-xs border-collapse">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase tracking-wider font-semibold">
                            <th className="py-3 px-4">ชื่อ - นามสกุล</th>
                            <th className="py-3 px-4">เบอร์โทรศัพท์</th>
                            <th className="py-3 px-4">สถานะปัจจุบัน</th>
                            <th className="py-3 px-4">กำหนดตำแหน่ง</th>
                            <th className="py-3 px-4 text-right">การจัดการ</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {candidateUsers.map((user) => {
                            const initial = (user.firstName || user.name || "U").charAt(0).toUpperCase();
                            const isAlreadyAdmin = Boolean(user.admin);
                            const roleInfo = getRoleInfo(user.role, configs);

                            return (
                              <tr key={user.id || user.userId || user.phone} className="hover:bg-slate-50/80 transition-colors">
                                <td className="py-3 px-4">
                                  <div className="flex items-center gap-2.5">
                                    {user.profileImage ? (
                                      <Avatar className="w-8 h-8 rounded-md border border-slate-200">
                                        <AvatarImage src={user.profileImage} />
                                      </Avatar>
                                    ) : (
                                      <div className="w-8 h-8 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-slate-700">
                                        {initial}
                                      </div>
                                    )}
                                    <div>
                                      <p className="font-semibold text-slate-900">
                                        {user.firstName} {user.lastName}
                                      </p>
                                      <p className="text-[10px] text-slate-400 font-mono">#{user.userId || user.id}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3 px-4 font-mono font-semibold text-slate-700">
                                  {user.phone || "-"}
                                </td>
                                <td className="py-3 px-4">
                                  {isAlreadyAdmin ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      <ShieldCheck className="w-3 h-3 text-emerald-600" />
                                      เป็นแอดมินแล้ว ({roleInfo.name})
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                                      ลูกค้าทั่วไป (User)
                                    </span>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  <select
                                    disabled={user.role === "dev" || user.role?.toLowerCase() === "ceo" || actionLoadingId === user.id}
                                    value={user.role || "admin"}
                                    onChange={(e) => handleEditRole(user.id, e.target.value)}
                                    className="bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-md px-2.5 py-1 focus:outline-none focus:border-blue-400 disabled:opacity-60"
                                  >
                                    {allRoles.map((r) => (
                                      <option key={r.key} value={r.key}>
                                        {r.name}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="py-3 px-4 text-right">
                                  {isAlreadyAdmin ? (
                                    user.role === "dev" || user.role?.toLowerCase() === "ceo" ? (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-100 text-slate-400 text-[11px] font-semibold">
                                        <Lock className="w-3 h-3" /> ล็อกสิทธิ์ {user.role === "dev" ? "Dev" : "CEO"}
                                      </span>
                                    ) : (
                                      <button
                                        disabled={actionLoadingId === user.id}
                                        onClick={() => handleToggleAdminStatus(user, false)}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-md text-xs font-semibold transition-colors disabled:opacity-50"
                                      >
                                        <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                                        <span>ลบสิทธิ์แอดมิน</span>
                                      </button>
                                    )
                                  ) : (
                                    <button
                                      disabled={actionLoadingId === user.id}
                                      onClick={() => handleToggleAdminStatus(user, true)}
                                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-bold transition-colors shadow-xs disabled:opacity-50"
                                    >
                                      <UserPlus className="w-3.5 h-3.5" />
                                      <span>แต่งตั้งเป็นแอดมิน</span>
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Sub-Component: Mobile Card View for Admin Staff
const AdminMobileCard = ({
  user,
  actionLoading,
  onEditRole,
  onRevokeAdmin,
  allRoles = DEFAULT_MASTER_ROLES,
  configs = {},
}) => {
  const isDev = user.role === "dev";
  const isCeo = user.role?.toLowerCase() === "ceo";
  const isProtected = isDev || isCeo;
  const initial = (user.firstName || user.name || "A").charAt(0).toUpperCase();
  const roleInfo = getRoleInfo(user.role, configs);
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.name || "Admin";

  return (
    <div className="bg-white border border-slate-200 rounded-md p-3.5 space-y-3 shadow-xs">
      {/* 1. Header: Avatar, Name, and Role Badge */}
      <div className="flex items-start justify-between gap-2.5">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {user.profileImage ? (
            <Avatar className="w-10 h-10 rounded-md border border-slate-200 bg-slate-100 object-cover shrink-0">
              <AvatarImage src={user.profileImage} />
            </Avatar>
          ) : (
            <div className="w-10 h-10 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-slate-700 shrink-0">
              {initial}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="font-bold text-slate-900 text-sm truncate flex items-center gap-1.5">
              <span>{fullName}</span>
              {user.userId && <span className="text-[10px] text-slate-400 font-mono">#{user.userId}</span>}
            </p>
            {user.lineTitle && (
              <p className="text-[11px] text-emerald-700 font-medium truncate flex items-center gap-1 mt-0.5">
                <span className="font-bold text-[9px] px-1 py-0.2 bg-emerald-100 text-emerald-800 rounded">LINE</span>
                <span className="truncate">{user.lineTitle}</span>
              </p>
            )}
          </div>
        </div>

        {/* Role Badge */}
        <span
          className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-md border shadow-2xs shrink-0 ${roleInfo.color}`}
          title={roleInfo.desc}
        >
          {roleInfo.name}
        </span>
      </div>

      {/* 2. Contact details: Phone & Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs bg-slate-50/90 p-2.5 rounded-md border border-slate-100">
        <div className="flex items-center gap-1.5 text-slate-700">
          <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="text-slate-400 text-[11px]">เบอร์:</span>
          {user.phone ? (
            <a href={`tel:${user.phone}`} className="font-mono font-semibold text-blue-600 hover:underline">
              {user.phone}
            </a>
          ) : (
            <span className="text-slate-400">-</span>
          )}
        </div>
        {user.email && (
          <div className="flex items-center gap-1.5 text-slate-600 truncate">
            <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="truncate">{user.email}</span>
          </div>
        )}
      </div>

      {/* 3. Action Area */}
      <div className="pt-1">
        {isProtected ? (
          <div className="flex items-center justify-between py-1.5 px-3 bg-amber-50/80 border border-amber-200/80 rounded-md text-xs text-amber-800 font-semibold">
            <span className="flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5 text-amber-600" />
              <span>ตำแหน่งถูกล็อก ({isDev ? "Developer" : "CEO"})</span>
            </span>
            <span className="text-[11px] text-amber-600">ห้ามแก้ไข</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {/* Role Select Dropdown */}
            <div className="relative flex-1">
              <select
                disabled={actionLoading}
                value={user.role || "staff"}
                onChange={(e) => onEditRole(user.id, e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 hover:border-slate-300 text-slate-800 text-xs font-semibold rounded-md pl-2.5 pr-8 py-2 min-h-[38px] focus:outline-none focus:border-blue-400 disabled:opacity-50 appearance-none"
              >
                {allRoles.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>

            {/* Revoke Admin Button */}
            <button
              disabled={actionLoading}
              onClick={() => onRevokeAdmin(user)}
              className="px-3 py-2 bg-rose-50 hover:bg-rose-100 active:bg-rose-200 text-rose-700 border border-rose-200 rounded-md text-xs font-bold transition-colors min-h-[38px] shrink-0 flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
            >
              <Trash2 className="w-3.5 h-3.5 text-rose-600" />
              <span>ลบสิทธิ์</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Sub-Component: Mobile Card for Candidate User Search
const CandidateMobileCard = ({
  user,
  actionLoadingId,
  handleEditRole,
  handleToggleAdminStatus,
  allRoles = DEFAULT_MASTER_ROLES,
  configs = {},
}) => {
  const initial = (user.firstName || user.name || "U").charAt(0).toUpperCase();
  const isAlreadyAdmin = Boolean(user.admin);
  const roleInfo = getRoleInfo(user.role, configs);
  const isDev = user.role === "dev";
  const isCeo = user.role?.toLowerCase() === "ceo";
  const isProtected = isDev || isCeo;

  return (
    <div className="bg-white border border-slate-200 rounded-md p-3.5 space-y-3 shadow-xs">
      <div className="flex items-start justify-between gap-2.5">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {user.profileImage ? (
            <Avatar className="w-10 h-10 rounded-md border border-slate-200 object-cover shrink-0">
              <AvatarImage src={user.profileImage} />
            </Avatar>
          ) : (
            <div className="w-10 h-10 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-slate-700 shrink-0">
              {initial}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="font-bold text-slate-900 text-sm truncate">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-[10px] text-slate-400 font-mono">#{user.userId || user.id}</p>
          </div>
        </div>

        {isAlreadyAdmin ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
            <ShieldCheck className="w-3 h-3 text-emerald-600" />
            {roleInfo.name}
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200 shrink-0">
            ลูกค้าทั่วไป
          </span>
        )}
      </div>

      <div className="bg-slate-50/90 p-2.5 rounded-md border border-slate-100 text-xs text-slate-700">
        <span className="text-slate-400 text-[11px]">เบอร์: </span>
        <span className="font-mono font-semibold">{user.phone || "-"}</span>
      </div>

      <div className="pt-1 flex flex-col gap-2">
        <div className="relative">
          <select
            disabled={isProtected || actionLoadingId === user.id}
            value={user.role || "admin"}
            onChange={(e) => handleEditRole(user.id, e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 text-slate-800 text-xs font-semibold rounded-md pl-2.5 pr-8 py-2 min-h-[38px] focus:outline-none focus:border-blue-400 disabled:opacity-60 appearance-none"
          >
            {allRoles.map((r) => (
              <option key={r.key} value={r.key}>
                {r.name}
              </option>
            ))}
          </select>
          <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>

        <div>
          {isAlreadyAdmin ? (
            isProtected ? (
              <div className="flex items-center justify-center gap-1 py-2 px-2.5 rounded-md bg-slate-100 text-slate-400 text-xs font-semibold">
                <Lock className="w-3.5 h-3.5" />
                <span>ล็อกสิทธิ์ {isDev ? "Developer" : "CEO"}</span>
              </div>
            ) : (
              <button
                disabled={actionLoadingId === user.id}
                onClick={() => handleToggleAdminStatus(user, false)}
                className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-md text-xs font-bold transition-colors min-h-[38px] disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                <span>ลบสิทธิ์แอดมิน</span>
              </button>
            )
          ) : (
            <button
              disabled={actionLoadingId === user.id}
              onClick={() => handleToggleAdminStatus(user, true)}
              className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-bold transition-colors shadow-xs min-h-[38px] disabled:opacity-50"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>แต่งตั้งเป็นแอดมิน</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Sub-Component: Reusable Table Row for Admins
const AdminTableRow = ({
  user,
  actionLoading,
  onEditRole,
  onRevokeAdmin,
  currentProfile,
  allRoles = DEFAULT_MASTER_ROLES,
  configs = {},
}) => {
  const isDev = user.role === "dev";
  const isCeo = user.role?.toLowerCase() === "ceo";
  const isProtected = isDev || isCeo;
  const initial = (user.firstName || user.name || "A").charAt(0).toUpperCase();
  const roleInfo = getRoleInfo(user.role, configs);
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.name || "Admin";

  return (
    <tr className="hover:bg-slate-50/80 transition-colors">
      {/* 1. Name & Avatar */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-2.5">
          {user.profileImage ? (
            <Avatar className="w-8 h-8 rounded-md border border-slate-200 bg-slate-100 object-cover">
              <AvatarImage src={user.profileImage} />
            </Avatar>
          ) : (
            <div className="w-8 h-8 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-slate-700">
              {initial}
            </div>
          )}
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
              <span>{fullName}</span>
              {user.userId && <span className="text-[10px] text-slate-400 font-mono">#{user.userId}</span>}
            </p>
            {user.lineTitle && (
              <p className="text-[10px] text-emerald-700 font-medium truncate flex items-center gap-1">
                <span className="font-bold text-[9px] px-1 py-0.2 bg-emerald-100 text-emerald-800 rounded">LINE</span>
                <span className="truncate">{user.lineTitle}</span>
              </p>
            )}
          </div>
        </div>
      </td>

      {/* 2. Phone */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-1 text-slate-700 font-mono font-medium text-xs">
          <Phone className="w-3 h-3 text-slate-400 flex-shrink-0" />
          <span>{user.phone || "-"}</span>
        </div>
      </td>

      {/* 3. Email */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-1 text-slate-600 text-xs">
          {user.email ? (
            <>
              <Mail className="w-3 h-3 text-slate-400 flex-shrink-0" />
              <span className="truncate max-w-[180px]" title={user.email}>
                {user.email}
              </span>
            </>
          ) : (
            <span className="text-slate-400">-</span>
          )}
        </div>
      </td>

      {/* 4. Role Select */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <select
            disabled={isProtected || actionLoading}
            value={user.role || ""}
            onChange={(e) => onEditRole(user.id, e.target.value)}
            className={`text-xs rounded-md px-2.5 py-2 border font-semibold focus:outline-none focus:border-blue-400 cursor-pointer disabled:cursor-not-allowed disabled:opacity-65 min-h-[36px] ${roleInfo.color}`}
          >
            <option value="">-- ไม่ระบุ --</option>
            {allRoles.map((r) => (
              <option key={r.key} value={r.key}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
      </td>

      {/* 5. Action (Revoke Admin) */}
      <td className="py-3 px-4 text-right">
        {isProtected ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-slate-100 text-slate-400 text-[11px] font-semibold min-h-[36px]">
            <Lock className="w-3 h-3" /> ล็อกสิทธิ์ {isDev ? "Dev" : "CEO"}
          </span>
        ) : (
          <button
            disabled={actionLoading}
            onClick={() => onRevokeAdmin(user)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-xs font-semibold transition-colors disabled:opacity-50 min-h-[36px]"
            title="ลบสิทธิ์แอดมินของผู้ใช้นี้"
          >
            <Trash2 className="w-3.5 h-3.5 text-rose-600" />
            <span>ลบสิทธิ์</span>
          </button>
        )}
      </td>
    </tr>
  );
};

// Sub-Component: Team Accordion Item with Permissions View
const TeamAccordionItem = ({
  roleItem,
  teamUsers,
  actionLoadingId,
  onEditRole,
  onRevokeAdmin,
  currentProfile,
  allRoles = DEFAULT_MASTER_ROLES,
  configs = {},
  isDev = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
      {/* Accordion Header */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer hover:bg-slate-50/80 transition-colors"
      >
        <div className="flex items-center gap-2.5 sm:gap-3">
          <div className={`p-2 rounded-md border shrink-0 ${roleItem.color}`}>
            <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-bold text-slate-900 text-xs sm:text-sm">{roleItem.name}</h4>
              <span className={`px-2 py-0.2 rounded-md text-[10px] font-bold border ${roleItem.color}`}>
                {roleItem.key}
              </span>
            </div>
            <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5 truncate sm:whitespace-normal">{roleItem.desc}</p>
          </div>
        </div>

        <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
          <div className="flex items-center -space-x-1.5 overflow-hidden">
            {teamUsers.slice(0, 4).map((u) => (
              <div
                key={u.id || u.phone}
                className="w-7 h-7 rounded-md bg-slate-100 border-2 border-white flex items-center justify-center font-bold text-[10px] text-slate-700"
                title={`${u.firstName} ${u.lastName}`}
              >
                {(u.firstName || "A").charAt(0).toUpperCase()}
              </div>
            ))}
          </div>
          <span className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-bold border border-slate-200 shrink-0">
            {teamUsers.length} คน
          </span>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </div>

      {/* Accordion Content */}
      {isOpen && (
        <div className="p-3 sm:p-4 border-t border-slate-200 bg-slate-50/30 space-y-4">
          {/* Members Table */}
          <div className="space-y-2">
            <h5 className="font-bold text-xs text-slate-800 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-blue-600" />
              สมาชิกในทีม ({teamUsers.length} คน):
            </h5>
            {teamUsers.length === 0 ? (
              <p className="text-xs text-slate-400 py-3 text-center bg-white rounded-md border border-slate-200">
                ยังไม่มีสมาชิกในตำแหน่งนี้
              </p>
            ) : (
              <>
                {/* Mobile View (< md) (Dev only) */}
                {isDev && (
                  <div className="space-y-2.5 block md:hidden">
                    {teamUsers.map((user) => (
                      <AdminMobileCard
                        key={user.id || user.phone}
                        user={user}
                        actionLoading={actionLoadingId === user.id}
                        onEditRole={onEditRole}
                        onRevokeAdmin={onRevokeAdmin}
                        allRoles={allRoles}
                        configs={configs}
                      />
                    ))}
                  </div>
                )}

                {/* Desktop View (>= md, or always for non-dev) */}
                <div className={`${isDev ? "hidden md:block" : "block"} bg-white border border-slate-200 rounded-md overflow-hidden`}>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[650px] text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase font-semibold">
                          <th className="py-2.5 px-3">ชื่อ - นามสกุล</th>
                          <th className="py-2.5 px-3">เบอร์โทร</th>
                          <th className="py-2.5 px-3">อีเมล</th>
                          <th className="py-2.5 px-3">เปลี่ยนตำแหน่ง</th>
                          <th className="py-2.5 px-3 text-right">จัดการ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {teamUsers.map((user) => (
                          <AdminTableRow
                            key={user.id || user.phone}
                            user={user}
                            actionLoading={actionLoadingId === user.id}
                            onEditRole={onEditRole}
                            onRevokeAdmin={onRevokeAdmin}
                            currentProfile={currentProfile}
                            allRoles={allRoles}
                            configs={configs}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Permissions Matrix */}
          <div className="space-y-2">
            <h5 className="font-bold text-xs text-slate-800 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
              สิทธิ์การเข้าถึงหน้าต่างต่าง ๆ ในระบบของตำแหน่ง "{roleItem.name}":
            </h5>
            <div className="p-3 bg-white border border-slate-200 rounded-md max-h-72 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {pages
                  .filter((p) => !p.hideFromPermission)
                  .map((page) => {
                    const hasPageAccess = hasPagePermission(page, { role: roleItem.key }, configs);
                    return (
                      <div key={page.path} className="p-2 bg-slate-50/80 rounded-md border border-slate-100 space-y-1.5">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              hasPageAccess ? "bg-emerald-500" : "bg-slate-300"
                            }`}
                          ></span>
                          <span className={`text-xs font-bold ${hasPageAccess ? "text-slate-900" : "text-slate-400"}`}>
                            {page.name}
                          </span>
                        </div>
                        {page.children && page.children.length > 0 && (
                          <div className="pl-4 space-y-1 border-l border-slate-200 ml-1">
                            {page.children.map((child) => {
                              const hasChildAccess = hasPagePermission(child, { role: roleItem.key }, configs);
                              return (
                                <div key={child.path} className="flex items-center gap-1.5 text-[11px]">
                                  <span
                                    className={`w-1.5 h-1.5 rounded-full ${
                                      hasChildAccess ? "bg-emerald-500" : "bg-slate-300"
                                    }`}
                                  ></span>
                                  <span className={hasChildAccess ? "text-slate-700 font-medium" : "text-slate-400"}>
                                    {child.name}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admins;
